import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useChatStore, BeeLogChunk } from '../../stores/useChatStore.js';
import { Eye, EyeOff, Loader2, ChevronDown, ChevronUp, Bug } from 'lucide-react';
import MessageRenderer from './MessageRenderer.js';
import { arcWebhookHandler, WebhookCleanup } from '../../api/arcWebhookHandler.js';
import { arcTelemetry } from '../../api/arcTelemetry.js';
import '../../styles/chat-history.css';

function LogCard({ log }: { log: BeeLogChunk[] }) {
    return (
        <div className="mb-4 p-4 bg-gray-800 rounded border border-gray-700">
            {log.map((chunk, i) => (
                <section key={i} className={`mb-2 chunk--${chunk.phase}`}>
                    {chunk.title && (
                        <h5 className="font-semibold text-gray-200 mb-1">{chunk.title}</h5>
                    )}
                    {chunk.timestamp && (
                        <time className="text-xs opacity-70 text-gray-400">{chunk.timestamp}</time>
                    )}
                    {chunk.entries && (
                        <ul className="list-disc list-inside text-gray-300 mt-1">
                            {chunk.entries.map((e, j) => (
                                <li key={j}>{e}</li>
                            ))}
                        </ul>
                    )}
                    {chunk.content && (
                        <p className="mt-1 text-gray-300 whitespace-pre-line">{chunk.content}</p>
                    )}
                </section>
            ))}
        </div>
    );
}

const ChatHistory = () => {
    const {
        messages,
        isLoading,
        sessionId,
        injectWebhookResult,
        executeCommand,
        // Phase C: Message pagination state
        hasMoreMessages,
        isLoadingOlder,
        loadOlderMessages,
    } = useChatStore();
    const [showFeedback, setShowFeedback] = useState(true);
    // Debug mode: when false (default), assistant-log messages are hidden from chat
    // Operators can enable this to see system decision-making logs
    const [showDebugLogs, setShowDebugLogs] = useState(false);

    /**
     * Scroll-to-bottom logic:
     * - We consider the user "at bottom" when within BOTTOM_THRESHOLD_PX of the end.
     * - While at bottom, new messages (including streaming chunks) keep the view pinned.
     * - As soon as the user scrolls up, we stop auto-scrolling and instead show a down-arrow.
     * - While scrolled up, we count newly appended messages as "unseen" and show a badge.
     */
    const BOTTOM_THRESHOLD_PX = 50;
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const isAtBottomRef = useRef<boolean>(true);
    const [isAtBottom, setIsAtBottom] = useState<boolean>(true);
    const unseenCountRef = useRef<number>(0);
    const [unseenCount, setUnseenCount] = useState<number>(0);
    const prevMessageMetaRef = useRef<{
        count: number;
        firstId?: string;
        lastId?: string;
    }>({ count: 0 });

    // Track if event listeners have been initialized to prevent duplicate subscriptions
    const eventListenersInitialized = useRef(false);
    // Store cleanup functions for proper teardown on unmount
    const cleanupFunctionsRef = useRef<WebhookCleanup[]>([]);

    const updateScrollState = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const atBottomNow = distanceFromBottom <= BOTTOM_THRESHOLD_PX;

        if (atBottomNow !== isAtBottomRef.current) {
            isAtBottomRef.current = atBottomNow;
            setIsAtBottom(atBottomNow);
        }

        if (atBottomNow && unseenCountRef.current !== 0) {
            unseenCountRef.current = 0;
            setUnseenCount(0);
        }
    }, []);

    const scrollToBottom = useCallback((behavior: ScrollBehavior) => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }, []);

    const handleScroll = useCallback(() => {
        updateScrollState();
    }, [updateScrollState]);

    useEffect(() => {
        // Initialize event listeners once per component mount
        if (!eventListenersInitialized.current) {
            eventListenersInitialized.current = true;
            const effectiveSessionId = sessionId || 'anonymous';

            // Subscribe to webhook events (MissionBridge lifecycle, PR events, CI status, etc.)
            // These events are formatted as pluginCard messages with colored status badges
            // Note: Mock events only fire when VITE_ENABLE_UI_MOCK_EVENTS=true
            const webhookCleanup = arcWebhookHandler.listenForWebhooks(
                effectiveSessionId,
                result => {
                    if (result.shouldInject) {
                        injectWebhookResult(result.message);
                    }
                }
            );
            cleanupFunctionsRef.current.push(webhookCleanup);

            // Subscribe to CI telemetry events (started, success, failure)
            // These provide real-time feedback on build/test status
            // Note: Mock CI events only fire when VITE_ENABLE_UI_MOCK_EVENTS=true
            const isMockEventsEnabled =
                (import.meta as any).env?.VITE_ENABLE_UI_MOCK_EVENTS === 'true';
            if (isMockEventsEnabled) {
                arcTelemetry.simulateCIEvents(message => {
                    injectWebhookResult(message);
                });
            }
        }

        return () => {
            // Clean up all subscriptions and pending timeouts on unmount
            cleanupFunctionsRef.current.forEach(cleanup => cleanup());
            cleanupFunctionsRef.current = [];
            // Reset so re-mount can re-subscribe
            eventListenersInitialized.current = false;
        };
    }, [sessionId, injectWebhookResult]);

    useEffect(() => {
        const handleRetryEvent = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { taskId } = customEvent.detail;
            executeCommand(`/retry ${taskId}`);
        };

        const handleRecallEvent = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { taskId } = customEvent.detail;
            executeCommand(`/recall ${taskId}`);
        };

        window.addEventListener('beehive:retry', handleRetryEvent);
        window.addEventListener('beehive:recall', handleRecallEvent);

        return () => {
            window.removeEventListener('beehive:retry', handleRetryEvent);
            window.removeEventListener('beehive:recall', handleRecallEvent);
        };
    }, [executeCommand]);

    // Keep pinned to bottom during streaming/new messages *only if* user is already at bottom.
    // If user scrolls up, do not auto-scroll; instead increment unseen message count.
    useLayoutEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const firstId = messages[0]?.id;
        const lastId = messages[messages.length - 1]?.id;
        const count = messages.length;
        const prev = prevMessageMetaRef.current;

        // Detect prepend (loading older messages): first id changes but last id stays the same.
        const didPrepend =
            prev.count > 0 &&
            count > prev.count &&
            prev.lastId === lastId &&
            prev.firstId !== undefined &&
            prev.firstId !== firstId;

        const didAppend = prev.count > 0 && prev.lastId !== undefined && prev.lastId !== lastId;

        if (!didPrepend) {
            if (isAtBottomRef.current) {
                // Use instant scroll for pinning to avoid "smooth" jitter while streaming.
                scrollToBottom('auto');
                unseenCountRef.current = 0;
                if (unseenCount !== 0) setUnseenCount(0);
            } else if (didAppend) {
                // Count newly appended messages while scrolled up.
                const delta = Math.max(1, count - prev.count);
                unseenCountRef.current += delta;
                setUnseenCount(unseenCountRef.current);
            }
        }

        prevMessageMetaRef.current = { count, firstId, lastId };
        // After DOM updates, re-check if we're at bottom (e.g., content height changes).
        updateScrollState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        messages.length,
        messages[0]?.id,
        messages[messages.length - 1]?.id,
        messages[messages.length - 1]?.content,
        isLoading,
        scrollToBottom,
        updateScrollState,
    ]);

    const handleScrollToBottomClick = useCallback(() => {
        unseenCountRef.current = 0;
        setUnseenCount(0);
        scrollToBottom('smooth');
        // `isAtBottom` will flip to true naturally as the scroll completes (via onScroll).
    }, [scrollToBottom]);

    // const mockMessages = [
    //   {
    //     id: '1',
    //     sender: 'system' as const,
    //     content: 'Bee Agent initialized. Ready for autonomous assistance.',
    //     type: 'text' as const,
    //     messageType: 'chat' as const,
    //     timestamp: new Date(Date.now() - 300000),
    //     buildCredits: 8
    //   },
    //   {
    //     id: '2',
    //     sender: 'user' as const,
    //     content: 'Help me analyze the current codebase structure',
    //     type: 'text' as const,
    //     messageType: 'chat' as const,
    //     timestamp: new Date(Date.now() - 240000),
    //     buildCredits: 12
    //   },
    //   {
    //     id: '3',
    //     sender: 'bee' as const,
    //     content: 'I\'ll analyze the codebase structure for you. Let me examine the project files and dependencies...',
    //     type: 'text' as const,
    //     messageType: 'chat' as const,
    //     timestamp: new Date(Date.now() - 180000),
    //     buildCredits: 24
    //   },
    //   {
    //     id: '4',
    //     sender: 'bee' as const,
    //     content: JSON.stringify({
    //       title: 'Pull Request Created',
    //       description: 'Successfully created PR #39 for UX Pack – Monaco shell with comprehensive workspace improvements',
    //       status: 'completed',
    //       data: {
    //         url: 'https://github.com/DDPMLS/echoctl-website/pull/39',
    //         number: 39
    //       },
    //       actions: ['View PR', 'Review Changes']
    //     }),
    //     type: 'pluginCard' as const,
    //     messageType: 'chat' as const,
    //     timestamp: new Date(Date.now() - 120000),
    //     buildCredits: 30
    //   },
    //   {
    //     id: '5',
    //     sender: 'bee' as const,
    //     content: JSON.stringify({
    //       title: 'CI Passed',
    //       description: 'All continuous integration checks passed successfully. Build completed in 2m 34s.',
    //       status: 'completed',
    //       data: {
    //         url: 'https://github.com/DDPMLS/echoctl-website/actions',
    //         duration: '2m 34s'
    //       },
    //       actions: ['View Logs', 'View Results']
    //     }),
    //     type: 'pluginCard' as const,
    //     messageType: 'chat' as const,
    //     timestamp: new Date(Date.now() - 60000),
    //     buildCredits: 25
    //   },
    //   {
    //     id: '6',
    //     sender: 'bee' as const,
    //     content: JSON.stringify({
    //       title: 'Deployment Successful',
    //       description: 'Application deployed successfully to staging environment. All health checks passed.',
    //       status: 'completed',
    //       data: {
    //         url: 'https://staging.echoctl.com',
    //         environment: 'staging'
    //       },
    //       actions: ['View App', 'Monitor', 'Check Logs']
    //     }),
    //     type: 'pluginCard' as const,
    //     messageType: 'chat' as const,
    //     timestamp: new Date(Date.now() - 30000),
    //     buildCredits: 28
    //   }
    // ];

    const displayMessages = messages;

    return (
        <div className="relative flex-1 overflow-hidden">
            <div
                ref={scrollContainerRef}
                data-devinid="chat-history"
                className="h-full flex-1 overflow-y-auto scrollbar-hidden p-4"
                onScroll={handleScroll}
            >
                <div className="flex items-center justify-between p-2 border-b border-accent-warm-light/30">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowFeedback(s => !s)}
                            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200"
                        >
                            {showFeedback ? (
                                <EyeOff className="w-4 h-4" />
                            ) : (
                                <Eye className="w-4 h-4" />
                            )}
                            {showFeedback ? 'Hide Feedback' : 'Show Feedback'}
                        </button>
                        <button
                            onClick={() => setShowDebugLogs(s => !s)}
                            className={`flex items-center gap-2 text-sm ${
                                showDebugLogs
                                    ? 'text-amber-400 hover:text-amber-300'
                                    : 'text-gray-400 hover:text-gray-200'
                            }`}
                            title="Toggle system decision logs visibility"
                        >
                            <Bug className="w-4 h-4" />
                            {showDebugLogs ? 'Hide Debug' : 'Show Debug'}
                        </button>
                    </div>
                </div>

                {/* Phase C: Load older messages button */}
                {hasMoreMessages && (
                    <div className="flex justify-center py-3 border-b border-accent-warm-light/20">
                        <button
                            onClick={() => loadOlderMessages()}
                            disabled={isLoadingOlder}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoadingOlder ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <ChevronUp className="w-4 h-4" />
                                    Load older messages
                                </>
                            )}
                        </button>
                    </div>
                )}

                {displayMessages.map((message, i) => {
                    if (message.messageType === 'feedback' && !showFeedback) {
                        return null;
                    }

                    // Run logs (type: 'run-log') are always visible - never hide them
                    // They provide important execution visibility

                    // Hide "Soma thought" cards unless debug mode is enabled
                    // These are messages that echo the prompt like "Here is what SOMA Noēsis is seeing right now"
                    // or "Picking up from our last step, here is the current SOMA Noēsis state"
                    // They are NOT the actual responses - just internal state visibility
                    const isSomaThoughtCard =
                        message.content &&
                        (message.content.includes('Here is what SOMA Noēsis is seeing') ||
                            message.content.includes('here is the current SOMA Noēsis state') ||
                            message.content.includes('Picking up from our last step'));

                    if (isSomaThoughtCard && !showDebugLogs) {
                        return null;
                    }

                    // Hide assistant-log decision messages unless debug mode is enabled
                    // But always show run logs (assistant-log with message.log)
                    if (message.type === 'assistant-log' && !message.log && !showDebugLogs) {
                        return null;
                    }

                    if (message.type === 'assistant-log' && message.log) {
                        return <LogCard key={message.id} log={message.log} />;
                    }

                    return <MessageRenderer key={message.id} message={message} index={i} />;
                })}

                {isLoading && (
                    <div className="flex items-center space-x-3 px-4 py-2 border-t border-accent-warm-light/30">
                        <div className="p-2 rounded-xl bg-muted/20">
                            <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                        <div className="bg-muted/20 text-foreground p-3 rounded-xl border border-muted">
                            <p className="text-base text-gray-100">Processing...</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Scroll-to-bottom control: appears when user is not at bottom */}
            {!isAtBottom && (
                <button
                    type="button"
                    onClick={handleScrollToBottomClick}
                    aria-label="Scroll to bottom"
                    className="absolute bottom-4 right-4 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-slate-800/90 text-slate-100 border border-slate-600 shadow-lg hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <ChevronDown className="w-5 h-5" />
                    {unseenCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-blue-600 text-white text-[11px] leading-5 text-center">
                            {unseenCount > 99 ? '99+' : unseenCount}
                        </span>
                    )}
                </button>
            )}
        </div>
    );
};

export default ChatHistory;
